import {
    Component,
    effect,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

export interface Check {
    hasMinLength?: boolean;
    hasNumber?: boolean;
    hasCapitalCase?: boolean;
    hasSmallCase?: boolean;
    hasSpecialCharacters?: boolean;
}

@Component({
    selector: 'app-password-check',
    templateUrl: './password-check.component.html',
    imports: [
        NgClass,
        MatIcon,
        TranslateModule,
    ],
})
export class PasswordCheckComponent {
    _password = '';
    password = input<string>();

    checkEv = output<Check>();
    onCorrect = output<boolean>();

    correct = signal(false);
    check = signal<Check | any>({});

    checks: { name: string; translationKey: string }[] = [
        {
            name: 'hasMinLength',
            translationKey: 'GENERAL.PASSWORD_CHECK.MIN_LENGTH',
        },
        {
            name: 'hasNumber',
            translationKey: 'GENERAL.PASSWORD_CHECK.HAS_NUMBER',
        },
        {
            name: 'hasCapitalCase',
            translationKey: 'GENERAL.PASSWORD_CHECK.HAS_CAPITAL_CASE',
        },
        {
            name: 'hasSmallCase',
            translationKey: 'GENERAL.PASSWORD_CHECK.HAS_SMALL_CASE',
        },
        {
            name: 'hasSpecialCharacters',
            translationKey: 'GENERAL.PASSWORD_CHECK.HAS_SPECIAL_CHARACTERS',
        },
        {
            name: 'hasNoRestrictedCharacters',
            translationKey:
                'GENERAL.PASSWORD_CHECK.HAS_NO_RESTRICTED_CHARACTERS',
        },
    ];

    constructor() {
        effect(() => {
            const password = this.password();
            // Run validation outside the reactive context: passwordValidation()
            // both reads and writes the `check`/`correct` signals, which would
            // otherwise make this effect depend on them and loop infinitely.
            untracked(() => {
                if (password != undefined) {
                    this._password = password;
                    this.passwordValidation();
                }
            });
        });
    }

    passwordValidation() {
        const password = this._password;
        let allCorrect = true;
        const checks:
            | {
                  hasMinLength?: boolean;
                  hasNumber?: boolean;
                  hasCapitalCase?: boolean;
                  hasSmallCase?: boolean;
                  hasSpecialCharacters?: boolean;
              }
            | any = {};

        if (password.match(/.{12,}/)) {
            checks.hasMinLength = true;
        } else {
            checks.hasMinLength = false;
        }

        if (password.match(/[0-9]/)) {
            checks.hasNumber = true;
        } else {
            checks.hasNumber = false;
        }

        if (password.match(/[A-Z]/)) {
            checks.hasCapitalCase = true;
        } else {
            checks.hasCapitalCase = false;
        }

        if (password.match(/[a-z]/)) {
            checks.hasSmallCase = true;
        } else {
            checks.hasSmallCase = false;
        }

        if (password.match(/[!@#$%^&*)(+=._-]+/)) {
            checks.hasSpecialCharacters = true;
        } else {
            checks.hasSpecialCharacters = false;
        }

        if (password.match(/^\S*$/)) {
            checks.hasNoRestrictedCharacters = true;
        } else {
            checks.hasNoRestrictedCharacters = false;
        }

        Object.keys(checks).forEach(key => {
            if (!checks[key]) {
                allCorrect = false;
            }
        });

        this.check.set({ ...this.check(), ...checks });

        setTimeout(() => {
            this.correct.set(allCorrect);
            this.onCorrect.emit(this.correct());
        });
    }
}
